import React, { useCallback } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage } from 'react-intl';
import { ClassNameMap } from '@mui/styles';
import { ProgressButton, ProgressButtonProps } from '../ProgressButton';

type SimpleDialogProps = {
  classes?: ClassNameMap;
  open: boolean;
  onSecondaryAction: () => void | Promise<void>;
  onPrimaryAction: () => void | Promise<void>;
  onClose: () => void | Promise<void>;
  title: React.ReactNode;
  primaryActionButtonTitle?: React.ReactNode;
  secondaryActionButtonTitle?: React.ReactNode;
  messages: React.ReactNode[];
  progressButtonProps?: ProgressButtonProps;
  closeOnBlur?: boolean;
};

const useStyles = makeStyles((theme) => ({
  title: {
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: theme.spacing(2.5),
    lineHeight: 1.6,
    fontFamily: theme.typography.h6.fontFamily
  },
  primaryButton: {
    color: theme.palette.common.white
  },
  messages: {
    color: theme.palette.text.secondary
  },
  root: {
    padding: theme.spacing(0)
  },
  secondaryButton: {
    color: theme.palette.secondary.main,
    marginRight: theme.spacing(1)
  }
}));

const defaultButtonTitles = {
  cancel: <FormattedMessage id="confirmationDialog.cancel" />,
  ok: <FormattedMessage id="confirmationDialog.yes" />
};

export const SimpleDialog: React.FC<SimpleDialogProps> = ({
  classes: overrideClasses,
  open,
  onSecondaryAction,
  onPrimaryAction,
  onClose,
  title,
  primaryActionButtonTitle = defaultButtonTitles.ok,
  secondaryActionButtonTitle = defaultButtonTitles.cancel,
  messages,
  progressButtonProps = { loading: false, color: 'primary' },
  closeOnBlur = true
}: SimpleDialogProps) => {
  const classes = useStyles({ classes: overrideClasses });
  const { loading: progressOkButtonLoading, color } = progressButtonProps;

  const voidFn = useCallback(() => null, []);

  return (
    <Dialog
      data-testid="simple-dialog"
      open={open}
      onClose={closeOnBlur ? onClose : voidFn}
      className={classes.root}
    >
      <DialogTitle className={classes.title}>{title}</DialogTitle>
      <DialogContent>
        {messages.map((message, index) => (
          <DialogContentText
            key={`message${index}`}
            className={classes.messages}
          >
            {message}
          </DialogContentText>
        ))}
      </DialogContent>
      <DialogActions>
        <Button
          data-testid="simple-secondary-action-btn"
          onClick={onSecondaryAction}
          color="secondary"
          className={classes.secondaryButton}
        >
          {secondaryActionButtonTitle}
        </Button>
        <ProgressButton
          data-testid="simple-primary-action-btn"
          variant="contained"
          color={color}
          disabled={progressOkButtonLoading}
          loading={progressOkButtonLoading}
          onClick={onPrimaryAction}
          className={classes.primaryButton}
        >
          {primaryActionButtonTitle}
        </ProgressButton>
      </DialogActions>
    </Dialog>
  );
};
